import type { RestaurantSiteConfig } from "@/lib/restaurant-platform";

function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  const normalized = digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;

  if (normalized.length !== 10) {
    return phone;
  }

  return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
}

function neighborhoodLabel(siteConfig: RestaurantSiteConfig): string {
  const { city, state } = siteConfig.address;
  if (siteConfig.restaurantId === "tuscan-cove") {
    return `Southern Highlands, ${city}`;
  }
  return `${city}, ${state}`;
}

function buildTuscanReservationFaqItems(
  siteConfig: RestaurantSiteConfig,
  phone: string,
) {
  const place = neighborhoodLabel(siteConfig);

  return [
    {
      question: `Do you take walk-ins at ${siteConfig.name}?`,
      answer:
        `Yes. ${siteConfig.name} accepts walk-ins in ${place}. Open tables are first-come, first-served, but weekend dinner and Sunday brunch fill quickly. Book on this page or call ${phone} to check wait times.`,
    },
    {
      question: "Do you take reservations for Sunday brunch?",
      answer:
        `Yes. Sunday brunch reservations are available at ${siteConfig.name}. Brunch is popular in ${place}, so booking a few days ahead is recommended. Reserve online here or call ${phone}.`,
    },
    {
      question: "How do I book a table for a large party?",
      answer:
        `For parties of 8 or more, call ${phone} so our team can confirm table fit and timing. For buyouts or custom menus, submit a Private Events inquiry and we'll follow up with options.`,
    },
    {
      question: "How far in advance should I make a reservation?",
      answer:
        `Book a few days ahead for weekend dinners and Sunday brunch. Those slots fill first at ${siteConfig.name}. Weeknight tables are often available on shorter notice. Call ${phone} for same-day openings.`,
    },
    {
      question: "Can I change or cancel my reservation?",
      answer:
        `Yes. Update or cancel as early as possible so we can release your table to other guests. Call ${phone} with your confirmation code, or use the contact details in your confirmation email.`,
    },
    {
      question: `Can I host a private event at ${siteConfig.name}?`,
      answer:
        `Yes. ${siteConfig.name} hosts rehearsal dinners, birthday parties, and corporate gatherings in ${place}. Start on our Private Events page: share your date, headcount, and menu preferences, and our team will follow up.`,
    },
  ];
}

function buildFirstPartyReservationFaqItems(
  siteConfig: RestaurantSiteConfig,
  phone: string,
) {
  return [
    {
      question: `How do I reserve at ${siteConfig.name}?`,
      answer:
        `Book on this page to pick your party size, date, and time. Confirmation is instant. Call ${phone} for same-day help.`,
    },
    {
      question: `Where is ${siteConfig.name}?`,
      answer:
        `${siteConfig.name} is located at ${siteConfig.address.line1}, ${siteConfig.address.city}, ${siteConfig.address.state} ${siteConfig.address.zip}.`,
    },
    {
      question: "How do I book a table for a large party?",
      answer:
        `For parties of 8 or more, call ${phone} so our team can confirm table fit and timing. For buyouts or custom menus, submit a Private Events inquiry and we'll follow up with options.`,
    },
    {
      question: "Can I change or cancel my reservation?",
      answer:
        `Yes. Update or cancel as early as possible so we can release your table to other guests. Call ${phone} with your confirmation code, or use the contact details in your confirmation email.`,
    },
    {
      question: `Can I host a private event at ${siteConfig.name}?`,
      answer:
        `Yes. ${siteConfig.name} hosts private gatherings. Start on our Private Events page: share your date, headcount, and menu preferences, and our team will follow up.`,
    },
  ];
}

export function buildReservationFaqItems(siteConfig: RestaurantSiteConfig) {
  const phone = formatPhone(siteConfig.contact.phone);

  if (siteConfig.restaurantId === "roma-kitchen") {
    return [
      {
        question: "How do I reserve at Chef's Roma Kitchen?",
        answer:
          "Choose Southern Highlands on this page, then pick your party size, date, and time to book directly with the restaurant. For Henderson, choose the Henderson tab to continue with Google Reserve.",
      },
      {
        question: "Which Roma locations can I reserve?",
        answer:
          "Both rooms take reservations. Roma Kitchen in Southern Highlands books directly on this page. Chef's Roma Kitchen in Henderson books through Google Reserve.",
      },
      {
        question: "Can I call Roma Kitchen for reservation help?",
        answer:
          `Yes. Call Southern Highlands at ${phone}, or choose the Henderson tab in the reservation shortcut for the Henderson phone number.`,
      },
      {
        question: "Can Roma handle larger groups or private dinners?",
        answer:
          "Yes. For private dining, catering, rehearsal dinners, birthdays, or corporate meals, use the private-events or catering page so the team gets the date, headcount, and service style.",
      },
    ];
  }

  if (siteConfig.reservationUrl.startsWith("http")) {
    return [
      {
        question: `How do I reserve at ${siteConfig.name}?`,
        answer:
          `Use the reservation link on this page or call ${phone} for help with a table.`,
      },
      {
        question: `Where is ${siteConfig.name}?`,
        answer:
          `${siteConfig.name} is located at ${siteConfig.address.line1}, ${siteConfig.address.city}, ${siteConfig.address.state} ${siteConfig.address.zip}.`,
      },
      {
        question: `Can I call ${siteConfig.name} for reservation help?`,
        answer: `Yes. Call ${phone} for reservation questions and same-day help.`,
      },
    ];
  }

  if (siteConfig.restaurantId === "tuscan-cove") {
    return buildTuscanReservationFaqItems(siteConfig, phone);
  }

  return buildFirstPartyReservationFaqItems(siteConfig, phone);
}

export function buildReservationPrepItems(siteConfig: RestaurantSiteConfig) {
  const isTuscan = siteConfig.restaurantId === "tuscan-cove";

  return [
    {
      label: "Best for",
      detail: isTuscan
        ? "Dinner, Sunday brunch, date night, and regular table reservations."
        : "Dinner, date night, and regular table reservations.",
    },
    {
      label: "Call for",
      detail: "Parties of 8+, same-day changes, and accessibility requests.",
    },
    {
      label: "Arrive ready",
      detail: isTuscan
        ? "Southern Highlands parking, bar seating, and patio availability vary by service."
        : `Parking and seating at ${siteConfig.name} in ${siteConfig.address.city} vary by service.`,
    },
    {
      label: "Plan next",
      detail: "Private events and catering use separate inquiry forms.",
    },
  ];
}

export function buildReservationsSeoCopy(siteConfig: RestaurantSiteConfig) {
  const phone = formatPhone(siteConfig.contact.phone);
  const cityState = `${siteConfig.address.city}, ${siteConfig.address.state}`;

  if (siteConfig.restaurantId === "roma-kitchen") {
    return {
      title: "Reservations at Chef's Roma Kitchen | Reserve Now",
      description:
        "Reserve a table at Roma Kitchen in Southern Highlands directly online, or at Chef's Roma Kitchen in Henderson with Google Reserve.",
      keywords: [
        "Chef's Roma Kitchen reservations",
        "Roma Kitchen reservations",
        "Google Reserve Roma Kitchen",
        "Italian restaurant reservations Las Vegas",
        "Italian restaurant reservations Henderson",
        "Southern Highlands Italian reservations",
      ],
      openGraphTitle: "Reserve at Chef's Roma Kitchen",
    };
  }

  return {
    title: "Reservations | Book Your Table",
    description:
      `Reserve your table at ${siteConfig.name} in ${cityState}. Book through Guest Manager on this page or call ${phone}.`,
    keywords: [
      `reservations ${cityState}`,
      `book table ${siteConfig.address.city}`,
      `restaurant reservations ${siteConfig.address.zip}`,
      `${siteConfig.name} reservations`,
      "Guest Manager reservations",
      `dinner reservations near me`,
      `restaurant booking ${siteConfig.address.city}`,
      `romantic dinner reservation ${siteConfig.address.city}`,
      ...(siteConfig.restaurantId === "tuscan-cove"
        ? ["Sunday brunch reservations Las Vegas", "date night reservations Las Vegas"]
        : [`date night reservations ${siteConfig.address.city}`]),
    ],
    openGraphTitle: `Make a Reservation | ${siteConfig.name}`,
  };
}

export function buildReservationWebPageJsonLd(siteConfig: RestaurantSiteConfig) {
  const reservationUrl = `${siteConfig.baseUrl}/reservations`;
  const reservationTarget = siteConfig.reservationUrl.startsWith("http")
    ? siteConfig.reservationUrl
    : reservationUrl;

  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${reservationUrl}#webpage`,
    name: `Reservations | ${siteConfig.name}`,
    description:
      `Reserve a table at ${siteConfig.name} in ${siteConfig.address.city}, ${siteConfig.address.state}.`,
    url: reservationUrl,
    image: siteConfig.heroImage || siteConfig.ogImage,
    about: {
      "@id": `${siteConfig.baseUrl}/#restaurant`,
    },
    mainEntity: {
      "@id": `${siteConfig.baseUrl}/#restaurant`,
    },
    potentialAction: {
      "@type": "ReserveAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: reservationTarget,
        actionPlatform: [
          "http://schema.org/DesktopWebPlatform",
          "http://schema.org/MobileWebPlatform",
        ],
      },
      result: {
        "@type": "Reservation",
        name: "Table Reservation",
      },
    },
  };
}
